import {
    Component,
    AfterViewInit,
    ViewChild,
    ElementRef,
    Input,
    Output,
    EventEmitter,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    HostListener,
} from "@angular/core";

import * as pdfjsLib from "pdfjs-dist/build/pdf";
import { readAnnotationData } from './models/annotation-data';

import {
    PdfAnnotation,
    HighlightAnnotation,
    InkAnnotation,
    NoteAnnotation,
    PdfPoint,
    PdfTool,
    PdfAnnotationsState,
    FreeTextAnnotation,
    PdfAnnotationLayerOption,
    PdfLayerVisibilityOption,
} from "./models/pdf-annotation.model";

(pdfjsLib as any).GlobalWorkerOptions.workerSrc =
    "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.5.207/pdf.worker.min.js";


@Component({
    selector: "app-pdf-viewer",
    templateUrl: "./pdf-viewer.component.html",
    styleUrls: ["./pdf-viewer.component.css"],
})
export class PdfViewerComponent implements AfterViewInit, OnChanges, OnDestroy {

    private undoStack: string[] = [];
    private redoStack: string[] = [];
    private historySnapshot = "[]";
    private historyEditKey: string | null = null;
    private historyEditTimer: any = null;
    private restoringHistory = false;

    // Selector de capa de la barra (solo grupos). Es presentación: el componente padre valida y aplica el cambio.
    @Input() annotationLayers: PdfAnnotationLayerOption[] = [];
    @Input() activeAnnotationLayer: string | null = null;
    @Output() annotationLayerSelect = new EventEmitter<string>();
    // Capa de solo lectura: solo queda el puntero; las anotaciones llegan como capa pasiva.
    @Input() readOnly = false;
    // Anotaciones de la capa activa de consulta: el panel las lista para verlas, nunca para editarlas.
    @Input() readOnlyAnnotations: PdfAnnotation[] = [];
    // La capa activa editable está oculta: no se dibuja ni se edita, pero sus datos no cambian.
    @Input() annotationsHidden = false;
    // Control «Visualizar»: solo cambia lo que se dibuja; el componente padre decide qué capas se ven.
    @Input() visibilityOptions: PdfLayerVisibilityOption[] = [];
    @Output() layerVisibilityChange = new EventEmitter<{ value: string; visible: boolean }>();
    // Acción «Compartir con este grupo» del panel; el componente padre comprueba permisos y hace la copia.
    @Input() canShareAnnotations = false;
    @Input() shareStatus: string | null = null;
    @Output() shareAnnotation = new EventEmitter<PdfAnnotation>();
    @ViewChild('layerPicker', { static: false }) private layerPickerRef: ElementRef<HTMLDetailsElement>;
    @ViewChild('visibilityPicker', { static: false }) private visibilityPickerRef: ElementRef<HTMLDetailsElement>;
    @Input() annotationShared: (annotation: PdfAnnotation) => boolean = () => false;

    private comparableAnnotations(snapshot: string): string {
        return JSON.stringify(JSON.parse(snapshot), (key, value) => key === "updatedAt" ? undefined : value);
    }

    private hasHistoryChange(): boolean {
        return this.comparableAnnotations(JSON.stringify(this.annotations)) !== this.comparableAnnotations(this.historySnapshot);
    }

    get canUndo(): boolean {
        return !this.isDragging && !this.editingLocked && (!!this.undoStack.length || (!!this.historyEditKey && this.hasHistoryChange()));
    }

    get canRedo(): boolean {
        return !this.isDragging && !this.editingLocked && !!this.redoStack.length && !(this.historyEditKey && this.hasHistoryChange());
    }

    private recordHistory() {
        if (this.restoringHistory || this.historyEditKey) { return; }
        const snapshot = JSON.stringify(this.annotations);
        if (this.comparableAnnotations(snapshot) !== this.comparableAnnotations(this.historySnapshot)) {
            this.undoStack.push(this.historySnapshot);
            if (this.undoStack.length > 30) { this.undoStack.shift(); }
            this.redoStack = [];
        }
        this.historySnapshot = snapshot;
    }

    beginAnnotationEdit(field: string) {
        if (!this.selectedAnnotation) { return; }
        const key = this.selectedAnnotation.id + ":" + field;
        if (this.historyEditKey !== key) {
            this.finishAnnotationEdit();
            this.historyEditKey = key;
        }
    }

    private scheduleHistoryEdit() {
        clearTimeout(this.historyEditTimer);
        this.historyEditTimer = setTimeout(() => this.finishAnnotationEdit(), 800);
    }

    finishAnnotationEdit() {
        clearTimeout(this.historyEditTimer);
        this.historyEditTimer = null;
        if (!this.historyEditKey) { return; }
        this.historyEditKey = null;
        const pendingSave = !!this.annotationEditTimeout;
        clearTimeout(this.annotationEditTimeout);
        this.annotationEditTimeout = null;
        this.recordHistory();
        if (pendingSave) { this.notifyAnnotationsChange(); }
    }

    private resetAnnotationHistory() {
        clearTimeout(this.historyEditTimer);
        clearTimeout(this.annotationEditTimeout);
        this.historyEditTimer = null;
        this.annotationEditTimeout = null;
        this.historyEditKey = null;
        this.undoStack = [];
        this.redoStack = [];
        this.historySnapshot = JSON.stringify(this.annotations);
    }

    undoAnnotations() { this.restoreAnnotationHistory(false); }
    redoAnnotations() { this.restoreAnnotationHistory(true); }

    focusViewerForShortcuts(event: MouseEvent) {
        const target = event.target as HTMLElement;
        if (target.closest('input, textarea, select, button, a, summary, [contenteditable]')) { return; }
        this.viewerContainerRef.nativeElement.focus({ preventScroll: true });
    }

    onHistoryKeydown(event: KeyboardEvent) {
        if (event.defaultPrevented || (event as KeyboardEvent & { isComposing?: boolean }).isComposing ||
            event.altKey) { return; }
        const target = event.target as HTMLElement;
        if (target.isContentEditable || target.closest('input, textarea, select, [contenteditable]')) { return; }
        const key = event.key.toLowerCase();
        if (key === 'delete' || key === 'supr') {
            if (!this.selectedAnnotationId || event.repeat) { return; }
            event.preventDefault();
            event.stopPropagation();
            this.deleteSelectedAnnotation();
            return;
        }
        if (!event.ctrlKey) { return; }
        const undo = key === 'z' && !event.shiftKey;
        const redo = (key === 'y' && !event.shiftKey) || (key === 'z' && event.shiftKey);
        if (!undo && !redo) { return; }
        event.preventDefault();
        event.stopPropagation();
        if (event.repeat) { return; }
        if (undo && this.canUndo) { this.undoAnnotations(); }
        if (redo && this.canRedo) { this.redoAnnotations(); }
    }

    private restoreAnnotationHistory(redo: boolean) {
        if (this.isDragging || this.editingLocked) { return; }
        this.finishAnnotationEdit();
        const source = redo ? this.redoStack : this.undoStack;
        if (!source.length) { return; }
        const destination = redo ? this.undoStack : this.redoStack;
        const selectedId = this.selectedAnnotationId;
        const panelWasVisible = this.isAnnotationPanelVisible;
        this.cancelAnnotationNavigation();
        clearTimeout(this.annotationEditTimeout);
        this.annotationEditTimeout = null;
        this.draftInk = null;
        this.draftHighlight = null;
        this.movingAnnotationId = null;
        this.moveStartPoint = null;
        this.resizingAnnotationId = null;
        this.resizingAnnotationType = null;
        this.resizeDirection = null;
        this.resizeStartPoint = null;
        destination.push(JSON.stringify(this.annotations));
        this.restoringHistory = true;
        try {
            this.annotations = JSON.parse(source.pop());
            this.historySnapshot = JSON.stringify(this.annotations);
            this.selectedAnnotation = this.annotations.find(item => item.id === selectedId) || null;
            this.selectedAnnotationId = this.selectedAnnotation ? this.selectedAnnotation.id : null;
            if (!this.selectedAnnotation && panelWasVisible) { this.isAnnotationsPanelOpen = true; }
            this.notifyAnnotationsChange();
        } finally {
            this.restoringHistory = false;
        }
    }

    isAnnotationsPanelOpen = false;
    annotationTypeFilter: "all" | PdfAnnotation["type"] = "all";
    readonly annotationTypeFilters: Array<{ value: "all" | PdfAnnotation["type"]; label: string }> = [
        { value: "all", label: "Todas" },
        { value: "highlight", label: "Subrayados" },
        { value: "ink", label: "Dibujos" },
        { value: "note", label: "Notas" },
        { value: "freeText", label: "Textos" },
    ];
    annotationNavigationError = "";
    private annotationNavigationRevision = 0;
    private annotationPageChangePending = false;

    get isAnnotationPanelVisible(): boolean {
        return this.isAnnotationsPanelOpen || !!(this.selectedAnnotation &&
            (this.selectedAnnotation.type === "note" || this.selectedAnnotation.type === "freeText"));
    }

    get annotationGroups(): Array<{ page: number; items: PdfAnnotation[] }> {
        const groups = new Map<number, PdfAnnotation[]>();
        // El panel lista solo la capa activa; si es de consulta, la recibe aparte y nunca la edita.
        (this.readOnly ? this.readOnlyAnnotations || [] : this.annotations).forEach(annotation => {
            if (this.annotationTypeFilter !== "all" && annotation.type !== this.annotationTypeFilter) { return; }
            if (!groups.has(annotation.page)) { groups.set(annotation.page, []); }
            groups.get(annotation.page).push(annotation);
        });
        return Array.from(groups.keys()).sort((a, b) => a - b)
            .map(page => ({ page, items: groups.get(page) }));
    }

    annotationLabel(annotation: PdfAnnotation): string {
        return { highlight: "Subrayado", ink: "Dibujo", note: "Nota", freeText: "Texto" }[annotation.type];
    }

    annotationExcerpt(annotation: PdfAnnotation): string {
        if (annotation.type !== "note" && annotation.type !== "freeText") { return ""; }
        const text = (annotation.text || "").trim().replace(/\s+/g, " ");
        return text ? (text.length > 100 && !this.readOnly ? text.slice(0, 100) + '…' : text)
            : (annotation.type === "note" ? "Nota sin texto" : "Texto vacío");
    }

    trackAnnotation(index: number, annotation: PdfAnnotation): string { return annotation.id; }
    trackAnnotationPage(index: number, group: { page: number }): number { return group.page; }

    cancelAnnotationNavigation() { this.annotationNavigationRevision++; }

    async toggleAnnotationsPanel() {
        if (this.isAnnotationsPanelOpen && !this.selectedAnnotation) {
            this.closeAnnotationsPanel();
            return;
        }
        this.backToAnnotations();
        await this.refreshAnnotationsPanelLayout();
    }

    closeAnnotationsPanel() {
        this.clearSelection();
        this.isAnnotationsPanelOpen = false;
        this.annotationNavigationError = "";
        this.refreshAnnotationsPanelLayout();
    }

    backToAnnotations() {
        this.cancelAnnotationNavigation();
        this.clearSelection();
        this.isAnnotationsPanelOpen = true;
        this.annotationNavigationError = "";
    }

    private waitForAnnotationLayout(): Promise<void> {
        return new Promise(resolve => requestAnimationFrame(() => resolve()));
    }

    private async refreshAnnotationsPanelLayout() {
        const request = ++this.annotationNavigationRevision;
        const documentRevision = this.documentRevision;
        const selected = this.selectedAnnotation;
        await this.waitForAnnotationLayout();
        if (request !== this.annotationNavigationRevision || documentRevision !== this.documentRevision) { return; }
        if (this.pdfDoc && this.zoomMode !== "custom") {
            const rendered = await this.renderCurrentPageWithZoomMode();
            if (selected && request === this.annotationNavigationRevision &&
                documentRevision === this.documentRevision && rendered === this.renderRevision) {
                const current = this.annotations.find(item => item.id === selected.id && item.page === this.currentPage);
                if (current) { this.selectAnnotation(null, current); }
            }
        }
    }

    async selectAnnotationFromList(annotation: PdfAnnotation) {
        const request = ++this.annotationNavigationRevision;
        const documentRevision = this.documentRevision;
        const id = annotation.id;
        const page = annotation.page;
        this.annotationNavigationError = "";
        if (!this.pdfDoc || !Number.isInteger(page) || page < 1 || page > this.totalPages) {
            this.annotationNavigationError = "La página de esta anotación no está disponible.";
            return;
        }
        this.isAnnotationsPanelOpen = true;
        await this.waitForAnnotationLayout();
        if (request !== this.annotationNavigationRevision || documentRevision !== this.documentRevision) { return; }
        const changedPage = this.currentPage !== page;
        this.annotationPageChangePending = this.annotationPageChangePending || changedPage;
        this.currentPage = page;
        this.pageInput = page;
        this.clearSelection();
        const rendered = await this.renderCurrentPageWithZoomMode();
        if (request !== this.annotationNavigationRevision || documentRevision !== this.documentRevision ||
            this.currentPage !== page || rendered !== this.renderRevision) { return; }
        if (this.editingLocked) {
            // Solo lectura o capa oculta: se abre su página sin seleccionarla ni abrir la edición.
            if (this.annotationPageChangePending) {
                this.annotationPageChangePending = false;
                this.pageChange.emit(page);
            }
            return;
        }
        const current = this.annotations.find(item => item.id === id && item.page === page);
        if (!current) { return; }
        this.selectAnnotation(null, current);
        if (this.annotationPageChangePending) {
            this.annotationPageChangePending = false;
            this.pageChange.emit(page);
        }
        await this.waitForAnnotationLayout();
        if (request !== this.annotationNavigationRevision || documentRevision !== this.documentRevision ||
            rendered !== this.renderRevision || this.selectedAnnotation !== current) { return; }
        this.scrollToAnnotation(current);
    }

    private scrollToAnnotation(annotation: PdfAnnotation) {
        const area = this.pdfAreaRef.nativeElement;
        const elements = Array.from(area.querySelectorAll("[data-annotation-id]"));
        const element = elements.find(item => item.getAttribute("data-annotation-id") === annotation.id);
        let rect: { left: number; top: number; width: number; height: number } = element
            ? element.getBoundingClientRect() : null;
        if (annotation.type === "ink" && (!rect || !rect.width || !rect.height)) {
            const points = annotation.points.filter(point => Number.isFinite(point.x) && Number.isFinite(point.y));
            if (!points.length) { return; }
            const canvas = this.canvasRef.nativeElement.getBoundingClientRect();
            const box = points.reduce((bounds, point) => ({
                left: Math.min(bounds.left, point.x), top: Math.min(bounds.top, point.y),
                right: Math.max(bounds.right, point.x), bottom: Math.max(bounds.bottom, point.y),
            }), { left: Infinity, top: Infinity, right: -Infinity, bottom: -Infinity });
            rect = { left: canvas.left + box.left * canvas.width, top: canvas.top + box.top * canvas.height,
                width: (box.right - box.left) * canvas.width,
                height: (box.bottom - box.top) * canvas.height };
        }
        if (!rect) { return; }
        const bounds = area.getBoundingClientRect();
        const margin = 24;
        area.scrollTop += rect.top - bounds.top - area.clientTop -
            Math.max(margin, (area.clientHeight - rect.height) / 2);
        area.scrollLeft += rect.left - bounds.left - area.clientLeft -
            Math.max(margin, (area.clientWidth - rect.width) / 2);
    }

    @ViewChild("searchTextLayer", { static: false })
    searchTextLayerRef: ElementRef<HTMLDivElement>;

    isSearchOpen = false;

    @ViewChild("searchInput", { static: false })
    set searchInput(input: ElementRef<HTMLInputElement>) {
        if (input) { input.nativeElement.focus(); }
    }

    toggleSearch() {
        this.isSearchOpen = !this.isSearchOpen;
        if (!this.isSearchOpen) {
            this.onSearchQueryChange("");
        }
    }

    searchQuery = "";
    searching = false;
    searchError = "";
    searchMatches: Array<{ page: number; start: number; end: number }> = [];
    activeSearchIndex = -1;
    searchRects: Array<{ left: number; top: number; width: number; height: number }> = [];
    private searchPages = new Map<number, Promise<any>>();
    private searchRevision = 0;
    private documentRevision = 0;
    private renderRevision = 0;
    private searchTimer: any = null;
    private pageRenderTask: any = null;
    private textRenderTask: any = null;
    private searchPageChangePending = false;

    private resetSearch() {
        this.cancelAnnotationNavigation();
        clearTimeout(this.searchTimer);
        this.searchRevision++;
        this.documentRevision++;
        this.renderRevision++;
        if (this.pageRenderTask) { this.pageRenderTask.cancel(); }
        if (this.textRenderTask) { this.textRenderTask.cancel(); }
        this.searchPages.clear();
        this.searchQuery = "";
        this.searching = false;
        this.searchError = "";
        this.searchMatches = [];
        this.activeSearchIndex = -1;
        this.searchRects = [];
        this.searchPageChangePending = false;
        if (this.searchTextLayerRef) {
            this.searchTextLayerRef.nativeElement.textContent = "";
        }
    }

    ngOnDestroy() {
        this.cancelPan();
        this.resetAnnotationHistory();
        this.resetSearch();
        this.pdfLoadingTasks.forEach(task => this.destroyPdfTask(task));
        this.pdfDoc = null;
        this.pdfSrc = null;
    }

    onSearchQueryChange(query: string) {
        this.searchQuery = query;
        clearTimeout(this.searchTimer);
        const revision = ++this.searchRevision;
        this.searchMatches = [];
        this.activeSearchIndex = -1;
        this.searchRects = [];
        this.searchError = "";
        this.searching = !!query.trim() && !!this.pdfDoc;
        if (this.searching) {
            this.searchTimer = setTimeout(() => this.searchCurrentPdf(revision), 250);
        }
    }

    private getSearchPage(pageNumber: number): Promise<any> {
        if (!this.searchPages.has(pageNumber)) {
            const doc = this.pdfDoc;
            this.searchPages.set(pageNumber, doc.getPage(pageNumber)
                .then(page => page.getTextContent({ normalizeWhitespace: true }))
                .then(content => {
                    let text = "";
                    const positions: Array<{ item: number; start: number; end: number }> = [];
                    content.items.forEach((item, index) => {
                        let offset = 0;
                        for (const character of item.str) {
                            const lowered = character.toLowerCase();
                            text += lowered;
                            for (let i = 0; i < lowered.length; i++) {
                                positions.push({ item: index, start: offset, end: offset + character.length });
                            }
                            offset += character.length;
                        }
                    });
                    return { content, text, positions };
                }));
        }
        return this.searchPages.get(pageNumber);
    }

    private async searchCurrentPdf(revision: number) {
        const query = this.searchQuery.trim().toLowerCase();
        if (!query || revision !== this.searchRevision) { return; }
        const pages = this.totalPages;
        const matches: Array<{ page: number; start: number; end: number }> = [];
        let hasText = false;
        try {
            for (let page = 1; page <= pages; page++) {
                if (revision !== this.searchRevision) { return; }
                const indexed = await this.getSearchPage(page);
                if (revision !== this.searchRevision) { return; }
                hasText = hasText || !!indexed.text.trim();
                let start = indexed.text.indexOf(query);
                while (start !== -1) {
                    matches.push({ page, start, end: start + query.length });
                    start = indexed.text.indexOf(query, start + query.length);
                }
            }
            this.searchMatches = matches;
            this.searching = false;
            if (!hasText) { this.searchError = "Este PDF no contiene texto extraíble."; }
            if (matches.length) { await this.showSearchMatch(0); }
        } catch (error) {
            if (revision !== this.searchRevision) { return; }
            this.searching = false;
            this.searchError = "No se ha podido completar la búsqueda en este PDF.";
        }
    }

    async nextSearchMatch(direction: number) {
        if (!this.searchMatches.length) { return; }
        const index = (this.activeSearchIndex + direction + this.searchMatches.length)
            % this.searchMatches.length;
        await this.showSearchMatch(index);
    }

    private async showSearchMatch(index: number) {
        this.cancelAnnotationNavigation();
        const match = this.searchMatches[index];
        if (!match) { return; }
        const revision = this.searchRevision;
        this.activeSearchIndex = index;
        const changedPage = this.currentPage !== match.page;
        this.searchPageChangePending = this.searchPageChangePending || changedPage;
        this.currentPage = match.page;
        this.pageInput = match.page;
        this.clearSelection();
        await this.renderCurrentPageWithZoomMode();
        if (revision !== this.searchRevision || this.activeSearchIndex !== index || this.currentPage !== match.page) { return; }
        if (this.searchPageChangePending) {
            this.searchPageChangePending = false;
            this.pageChange.emit(match.page);
        }
        const rect = this.searchRects[0];
        if (rect && this.pdfAreaRef) {
            const area = this.pdfAreaRef.nativeElement;
            const layer = this.searchTextLayerRef.nativeElement.getBoundingClientRect();
            const bounds = area.getBoundingClientRect();
            area.scrollTop += layer.top + rect.top - bounds.top - area.clientHeight / 2;
            area.scrollLeft += layer.left + rect.left - bounds.left - area.clientWidth / 2;
        }
    }

    private async renderSearchMatch(pageNumber: number, viewport: any, revision: number) {
        const match = this.searchMatches[this.activeSearchIndex];
        if (!match || match.page !== pageNumber || !this.searchTextLayerRef) { return; }
        const searchRevision = this.searchRevision;
        const indexed = await this.getSearchPage(pageNumber);
        if (revision !== this.renderRevision || searchRevision !== this.searchRevision) { return; }
        const layer = this.searchTextLayerRef.nativeElement;
        const divs: HTMLElement[] = [];
        this.textRenderTask = (pdfjsLib as any).renderTextLayer({
            textContent: indexed.content, container: layer, viewport, textDivs: divs,
        });
        await this.textRenderTask.promise;
        if (revision !== this.renderRevision || searchRevision !== this.searchRevision) { return; }
        // PDF.js inserta los spans dinámicamente, fuera de los estilos encapsulados de Angular.
        divs.forEach(div => {
            div.style.position = "absolute";
            div.style.whiteSpace = "pre";
            div.style.transformOrigin = "0% 0%";
        });
        const first = indexed.positions[match.start];
        const last = indexed.positions[match.end - 1];
        const bounds = layer.getBoundingClientRect();
        const rects = [];
        for (let i = first.item; i <= last.item; i++) {
            const node = divs[i] && divs[i].firstChild;
            if (!node || !divs[i].parentNode) { continue; }
            const range = document.createRange();
            range.setStart(node, i === first.item ? first.start : 0);
            range.setEnd(node, i === last.item ? last.end : node.textContent.length);
            Array.from(range.getClientRects()).forEach(rect => {
                if (rect.width && rect.height) {
                    rects.push({ left: rect.left - bounds.left, top: rect.top - bounds.top,
                        width: rect.width, height: rect.height });
                }
            });
        }
        this.searchRects = rects;
    }

    @ViewChild("canvas", { static: false })
    canvasRef!: ElementRef<HTMLCanvasElement>;

    @ViewChild("viewerContainer", { static: false })
    viewerContainerRef!: ElementRef<HTMLDivElement>;

    @ViewChild("pdfArea", { static: false })
    pdfAreaRef!: ElementRef<HTMLDivElement>;


    /*
     * =========================================================
     * ENTRADAS Y SALIDAS DEL COMPONENTE
     * =========================================================
     */


    /*
     * PDF recibido desde ModalViewerComponent.
     */
    @Input()
    pdfSrc: string | null = null;


    /*
     * Página inicial que debe mostrar el visor.
     *
     * ModalViewerComponent recupera esta información
     * de currentPage / currentPageGroup del capítulo.
     */
    @Input()
    initialPage: number = 1;

    @Input() chapterEntry: "first" | "last" | number | null = null;
    @Input() hasPreviousChapter = false;
    @Input() hasNextChapter = false;
    @Output() globalPageRequest = new EventEmitter<number>();
    @Output() chapterBoundary = new EventEmitter<number>();
    private changingChapter = false;
    loadingDocument = true;

    private requestAdjacentChapter(direction: number) {
        this.finishAnnotationEdit();
        this.changingChapter = true;
        this.chapterBoundary.emit(direction);
    }

    get navigationBlocked(): boolean { return this.loadingDocument || this.changingChapter; }

    @Input() pagination: { pageStart: number; pageEnd: number; bookPages: number } | null = null;
    pageRangeMessage = "";

    get hasGlobalPagination(): boolean {
        if (!this.pagination) { return false; }
        const start = Number(this.pagination.pageStart);
        const end = Number(this.pagination.pageEnd);
        const total = Number(this.pagination.bookPages);
        return Number.isInteger(start) && start > 0 && Number.isInteger(end) && end >= start &&
            Number.isInteger(total) && total >= end && this.totalPages > 0 &&
            end - start + 1 === this.totalPages;
    }

    toVisiblePage(page: number): number {
        return this.hasGlobalPagination ? Number(this.pagination.pageStart) + Number(page) - 1 : Number(page);
    }

    private toInternalPage(page: number): number {
        return this.hasGlobalPagination ? Number(page) - Number(this.pagination.pageStart) + 1 : Number(page);
    }

    get visibleTotalPages(): number {
        return this.hasGlobalPagination ? Number(this.pagination.bookPages) : this.totalPages;
    }

    get visiblePageInput(): number {
        return this.pageInput === null || this.pageInput === undefined ? this.pageInput : this.toVisiblePage(this.pageInput);
    }

    set visiblePageInput(value: number) {
        this.pageRangeMessage = "";
        this.pageInput = value === null || value === undefined ? value : this.toInternalPage(value);
    }



    /*
     * JSON de anotaciones correspondiente
     * al capítulo actual.
     *
     * Se deja como any porque actualmente
     * EdicionesEO puede devolver:
     *
     * - string JSON
     * - objeto {}
     * - null
     */
    @Input()
    annotationsJson: any = null;

    @Input() annotationContext = '';
    // Capas no activas: se dibujan sin selección y nunca entran en el historial ni en el JSON editable.
    @Input() passiveAnnotations: PdfAnnotation[] = [];
    passiveOnPage(type: string): any[] {
        return this.passiveAnnotations.filter(item => item.page === this.currentPage && item.type === type);
    }
    @Output() annotationsStateChange = new EventEmitter<{ json: string; context: string }>();

    // Selector de capa: los campos están al principio de la clase.
    get activeLayerOption(): PdfAnnotationLayerOption | null {
        return (this.annotationLayers || []).find(option => option.value === this.activeAnnotationLayer) || null;
    }

    trackLayerOption(index: number, option: PdfAnnotationLayerOption): string { return option.value; }

    selectAnnotationLayer(option: PdfAnnotationLayerOption): void {
        this.closeAnnotationLayerPicker(false);
        if (option.disabled || option.value === this.activeAnnotationLayer) { return; }
        this.annotationLayerSelect.emit(option.value);
    }

    closeAnnotationLayerPicker(returnFocus: boolean): void { this.closePicker(this.layerPickerRef, returnFocus); }

    closeVisibilityPicker(returnFocus: boolean): void { this.closePicker(this.visibilityPickerRef, returnFocus); }

    private closePicker(ref: ElementRef<HTMLDetailsElement>, returnFocus: boolean): void {
        const picker = ref && ref.nativeElement;
        if (!picker || !picker.open) { return; }
        picker.open = false;
        const trigger = picker.querySelector('summary');
        if (returnFocus && trigger) { trigger.focus(); }
    }

    @HostListener('document:mousedown', ['$event'])
    @HostListener('document:touchstart', ['$event'])
    onDocumentPointerDown(event: Event): void {
        [this.layerPickerRef, this.visibilityPickerRef].forEach(ref => {
            const picker = ref && ref.nativeElement;
            if (picker && picker.open && !picker.contains(event.target as Node)) { picker.open = false; }
        });
    }

    // Sin edición posible: capa de consulta o capa activa oculta en el PDF.
    get editingLocked(): boolean { return this.readOnly || this.annotationsHidden; }

    get hiddenLayerCount(): number { return (this.visibilityOptions || []).filter(option => !option.visible).length; }

    requestShare(annotation: PdfAnnotation): void {
        if (!this.canShareAnnotations || !annotation || this.annotationShared(annotation)) { return; }
        this.shareAnnotation.emit(annotation);
    }

    toggleLayerVisibility(option: PdfLayerVisibilityOption, visible: boolean): void {
        if (option.disabled || option.visible === visible) { return; }
        this.layerVisibilityChange.emit({ value: option.value, visible });
    }

    annotationsLoadError: string | null = null;
    pdfLoadError: string | null = null;
    private loadedAnnotationContext = '';
    get hasPendingAnnotationEdit(): boolean { return !!this.annotationEditTimeout; }


    /*
     * Eventos utilizados para comunicar
     * el visor con ModalViewerComponent.
     */
    @Output()
    pageChange = new EventEmitter<number>();

    @Output()
    annotationsChange = new EventEmitter<string>();


    /*
     * =========================================================
     * ESTADO INTERNO
     * =========================================================
     */

    private viewInitialized = false;


    /*
     * Último JSON de anotaciones emitido por este visor.
     *
     * El componente padre recibe annotationsChange y
     * posteriormente vuelve a pasar ese mismo JSON mediante
     * [annotationsJson].
     *
     * Guardándolo aquí podemos reconocer ese "eco"
     * y evitar recargar las anotaciones innecesariamente.
     */
    private lastEmittedAnnotationsJson: string | null = null;


    /*
     * Temporizador utilizado mientras se editan
     * notas o textos desde el panel lateral.
     *
     * Evita emitir un nuevo JSON por cada tecla pulsada.
     */
    private annotationEditTimeout: any = null;


    /*
     * Tiempo que esperamos desde la última modificación
     * antes de notificar al componente padre.
     */
    private readonly annotationEditDelay = 300;


    isFullscreen = false;

    pdfDoc: any = null;

    currentPage = 1;

    totalPages = 0;

    /*
     * Valor mostrado en el campo editable de página.
     * Se mantiene separado de currentPage para no navegar
     * mientras el usuario todavía está escribiendo.
     */
    pageInput = 1;

    scale = 1.2;

    /*
     * Modo de zoom actual.
     *
     * "custom" = zoom manual con + / -
     * "width"  = ajustar al ancho disponible
     * "page"   = ajustar la página completa
     */
    zoomMode:
        "custom" |
        "width" |
        "page" =
        "custom";

    annotations: PdfAnnotation[] = [];

    currentTool: PdfTool = "none";

    // Preferencia de grosor para nuevos dibujos.
    selectedStrokeWidth = 2;

    readonly strokeWidthOptions = [1, 2, 3, 4, 6, 8, 10];

    draftHighlight: HighlightAnnotation | null = null;

    draftInk: InkAnnotation | null = null;

    isDragging = false;

    startX = 0;

    startY = 0;

    movingAnnotationId: string | null = null;

    moveStartPoint: PdfPoint | null = null;

    moveOriginalX = 0;

    moveOriginalY = 0;

    /*
     * Estado utilizado para redimensionar anotaciones
     * con caja: subrayados y textos libres.
     */
    resizingAnnotationId: string | null = null;

    resizingAnnotationType:
        "highlight" | "freeText" | null =
        null;

    resizeDirection:
        "n" | "s" | "e" | "w" |
        "ne" | "nw" | "se" | "sw" |
        null =
        null;

    resizeStartPoint: PdfPoint | null = null;

    resizeOriginalX = 0;

    resizeOriginalY = 0;

    resizeOriginalW = 0;

    resizeOriginalH = 0;

    selectedAnnotationId: string | null = null;

    selectedAnnotation: PdfAnnotation | null = null;

    // Preferencia de color para nuevas anotaciones.
    selectedColor = "#ffeb3b";

    get propertyType(): PdfTool {
        return this.selectedAnnotation ? this.selectedAnnotation.type : this.currentTool;
    }

    get displayedStrokeWidth(): number {
        return this.selectedAnnotation && this.selectedAnnotation.type === "ink"
            ? this.selectedAnnotation.strokeWidth
            : this.selectedStrokeWidth;
    }

    get displayedColor(): string {
        if (!this.selectedAnnotation) {
            return this.selectedColor;
        }

        const color = this.selectedAnnotation.color;
        // Los subrayados guardan rgba; el input de color necesita hexadecimal.
        if (this.selectedAnnotation.type === "highlight") {
            const rgb = color.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
            if (rgb) {
                return "#" + rgb.slice(1, 4)
                    .map(value => Number(value).toString(16).padStart(2, "0"))
                    .join("");
            }
        }
        return color;
    }

    /*
     * Salida auxiliar utilizada únicamente por las herramientas
     * de depuración / desarrollo.
     */
    jsonOutput = "";


    /*
     * =========================================================
     * CICLO DE VIDA
     * =========================================================
     */

    async ngAfterViewInit() {

        this.viewInitialized = true;


        /*
         * Recuperamos la última página guardada
         * del capítulo antes de cargar el PDF.
         */
        this.currentPage =
            this.initialPage &&
            this.initialPage > 0
                ? this.initialPage
                : 1;

        this.pageInput =
            this.currentPage;


        /*
         * Cargamos las anotaciones iniciales
         * correspondientes al capítulo.
         */
        this.loadAnnotationsFromJson();


        /*
         * Si ya disponemos del PDF,
         * procedemos a cargarlo.
         */
        if (this.pdfSrc) {

            await this.loadPdf();
        }
    }


    async ngOnChanges(
        changes: SimpleChanges,
    ) {

        if (changes.pdfSrc || changes.pagination) { this.pageRangeMessage = ""; }
        if ((changes.readOnly || changes.annotationsHidden) && this.editingLocked) { this.currentTool = 'none'; this.clearSelection(); }

        if (changes.pdfSrc) {
            this.resetAnnotationHistory();
            this.cancelAnnotationNavigation();
            this.annotationPageChangePending = false;
            this.clearSelection();
            this.annotationNavigationError = "";
            if (!this.pdfSrc) { this.annotations = []; }
        }

        if (changes.pdfSrc && !this.pdfSrc) {
            this.resetSearch();
            this.pdfDoc = null;
            this.totalPages = 0;
        }

        /*
         * =====================================================
         * CAMBIO DE CAPÍTULO / CAMBIO DE PDF
         * =====================================================
         */

        if (
            changes.pdfSrc &&
            this.viewInitialized &&
            this.pdfSrc
        ) {

            /*
             * Al cambiar de capítulo abrimos la última
             * página guardada para ese capítulo.
             */
            this.currentPage =
                this.initialPage &&
                this.initialPage > 0
                    ? this.initialPage
                    : 1;

            this.pageInput =
                this.currentPage;


            /*
             * Al cambiar de capítulo eliminamos
             * cualquier selección existente.
             */
            this.clearSelection();


            /*
             * Cargamos las anotaciones asociadas
             * al nuevo capítulo.
             *
             * Esto evita que permanezcan en pantalla
             * las anotaciones del capítulo anterior.
             */
            this.loadAnnotationsFromJson();


            /*
             * Finalmente cargamos el nuevo PDF.
             */
            await this.loadPdf();
        }


        /*
         * =====================================================
         * CAMBIO DEL JSON DE ANOTACIONES
         * =====================================================
         *
         * Cuando este componente modifica una anotación:
         *
         * PdfViewerComponent
         *      ↓
         * annotationsChange
         *      ↓
         * ModalViewerComponent
         *      ↓
         * [annotationsJson]
         *      ↓
         * PdfViewerComponent
         *
         * Por tanto, el mismo JSON que acabamos
         * de generar vuelve al componente.
         *
         * Si lo cargásemos de nuevo ejecutaríamos
         * loadAnnotationsFromJson(), que a su vez
         * ejecuta clearSelection().
         *
         * Eso provocaría que desapareciese el panel
         * mientras estamos escribiendo una nota.
         */

        /*
         * Si cambia únicamente initialPage y el PDF ya
         * está cargado, navegamos a esa página.
         */
        if (
            changes.initialPage &&
            this.viewInitialized &&
            !changes.pdfSrc &&
            this.pdfDoc
        ) {

            const targetPage =
                this.initialPage &&
                this.initialPage > 0
                    ? this.initialPage
                    : 1;


            if (
                targetPage !==
                this.currentPage
            ) {

                this.currentPage =
                    targetPage;


                if (
                    this.currentPage >
                    this.totalPages
                ) {

                    this.currentPage =
                        1;
                }

                this.pageInput =
                    this.currentPage;


                await this.renderCurrentPageWithZoomMode();
            }
        }


        if (
            (changes.annotationsJson || changes.annotationContext) &&
            this.viewInitialized
        ) {

            const incomingJson =
                typeof this.annotationsJson === "string"
                    ? this.annotationsJson
                    : JSON.stringify(
                          this.annotationsJson,
                      );


            /*
             * Si es exactamente el JSON que acaba
             * de emitir este mismo visor,
             * NO volvemos a cargar las anotaciones.
             */
            if (
                incomingJson ===
                this.lastEmittedAnnotationsJson &&
                this.annotationContext === this.loadedAnnotationContext
            ) {

                /*
                 * Ya hemos reconocido el eco.
                 *
                 * Limpiamos esta referencia para que
                 * un cambio externo posterior sí pueda
                 * procesarse normalmente.
                 */
                this.lastEmittedAnnotationsJson =
                    null;

            } else {

                /*
                 * El cambio viene realmente de fuera,
                 * por ejemplo:
                 *
                 * - cambio de capítulo;
                 * - carga desde base de datos;
                 * - importación externa.
                 */
                this.loadAnnotationsFromJson();
            }
        }
    }


    /*
     * =========================================================
     * PDF
     * =========================================================
     */

    private pdfLoadingTasks = new Set<any>();

    private createPdfLoadingTask() {
        return (pdfjsLib as any).getDocument(this.pdfSrc);
    }

    private destroyPdfTask(task: any) {
        if (!this.pdfLoadingTasks.delete(task)) { return; }
        // PDFDocumentProxy.destroy() delega en esta misma tarea (PDF.js 2.5.207).
        try {
            Promise.resolve(task.destroy()).catch(error => console.error("Error liberando PDF:", error));
        } catch (error) {
            console.error("Error liberando PDF:", error);
        }
    }

    async loadPdf() {
        this.pdfLoadError = null;
        this.loadingDocument = true;
        this.changingChapter = false;

        this.resetSearch();
        const documentRevision = this.documentRevision;
        this.pdfDoc = null;
        this.totalPages = 0;

        if (!this.pdfSrc) {

            return;
        }


        let loadingTask: any = null;
        try {

            loadingTask = this.createPdfLoadingTask();
            this.pdfLoadingTasks.add(loadingTask);

            const doc = await loadingTask.promise;
            if (documentRevision !== this.documentRevision) {
                this.destroyPdfTask(loadingTask);
                return;
            }
            this.pdfDoc = doc;


            this.totalPages =
                this.pdfDoc.numPages;


            if (
                this.currentPage >
                this.totalPages
            ) {

                this.currentPage = 1;
            }

            if (this.chapterEntry) {
                const destination = this.chapterEntry === "last" ? this.pdfDoc.numPages :
                    this.chapterEntry === "first" ? 1 : Number(this.chapterEntry);
                if (Number.isInteger(destination) && destination >= 1 && destination <= this.pdfDoc.numPages) {
                    this.currentPage = destination;
                } else {
                    this.pageRangeMessage = "La página solicitada no está disponible en este PDF.";
                }
            }
            this.pageInput = this.currentPage;

            await this.renderPage(
                this.currentPage,
            );
            if (documentRevision !== this.documentRevision) { return; }
            this.loadingDocument = false;
            if (this.chapterEntry) { this.pageChange.emit(this.currentPage); }

        } catch (error) {
            if (loadingTask) { this.destroyPdfTask(loadingTask); }
            if (documentRevision !== this.documentRevision) { return; }

            this.loadingDocument = false;
            this.pdfLoadError = 'No se ha podido cargar el documento. No se guardarán cambios de este PDF.';
        }
    }


    async renderPage(
        pageNumber: number,
    ) {

        if (!this.pdfDoc) {

            return;
        }


        const revision = ++this.renderRevision;
        this.searchRects = [];
        if (this.textRenderTask) { this.textRenderTask.cancel(); }
        if (this.searchTextLayerRef) {
            this.searchTextLayerRef.nativeElement.textContent = "";
        }
        const previousTask = this.pageRenderTask;
        if (previousTask) {
            previousTask.cancel();
            try { await previousTask.promise; } catch (_) { /* Render sustituido. */ }
        }
        if (revision !== this.renderRevision) { return; }

        try {

            const page =
                await this.pdfDoc
                    .getPage(
                        pageNumber,
                    );

            if (revision !== this.renderRevision) { return; }

            const viewport =
                page.getViewport({
                    scale:
                        this.scale,
                });


            const canvas =
                this.canvasRef
                    .nativeElement;


            const context =
                canvas.getContext(
                    "2d",
                );


            if (!context) {

                console.error(
                    "No se pudo obtener el contexto 2D del canvas",
                );

                return;
            }


            canvas.width =
                viewport.width;

            canvas.height =
                viewport.height;


            this.pageRenderTask = page
                .render({
                    canvasContext:
                        context,

                    viewport:
                        viewport,
                });
            await this.pageRenderTask.promise;
            if (revision !== this.renderRevision) { return; }
            await this.renderSearchMatch(pageNumber, viewport, revision);
            if (revision === this.renderRevision) { return revision; }

        } catch (error) {

            if (revision !== this.renderRevision || error.name === "RenderingCancelledException") {
                return;
            }

            console.error(
                "Error renderizando página:",
                error,
            );
        }
    }


    /*
     * =========================================================
     * NAVEGACIÓN
     * =========================================================
     */

    async nextPage() {
        if (this.navigationBlocked) { return; }
        this.cancelAnnotationNavigation();

        if (
            this.currentPage >=
            this.totalPages
        ) {
            if (this.hasNextChapter) { this.requestAdjacentChapter(1); }
            return;
        }


        this.currentPage++;

        this.pageInput =
            this.currentPage;


        this.clearSelection();


        await this.renderCurrentPageWithZoomMode();


        this.pageChange.emit(
            this.currentPage,
        );
    }


    async prevPage() {
        if (this.navigationBlocked) { return; }
        this.cancelAnnotationNavigation();

        if (
            this.currentPage <= 1
        ) {
            if (this.hasPreviousChapter) { this.requestAdjacentChapter(-1); }
            return;
        }


        this.currentPage--;

        this.pageInput =
            this.currentPage;


        this.clearSelection();


        await this.renderCurrentPageWithZoomMode();


        this.pageChange.emit(
            this.currentPage,
        );
    }


    /*
     * Navega directamente a la página escrita
     * en el campo editable de la toolbar.
     */
    async goToPage() {
        if (this.navigationBlocked) { return; }
        this.cancelAnnotationNavigation();

        if (
            !this.pdfDoc ||
            this.totalPages <= 0
        ) {

            this.pageInput =
                this.currentPage;

            return;
        }


        /*
         * Si el usuario borra temporalmente el contenido
         * del campo, mantenemos la página actual.
         */
        if (
            this.pageInput === null ||
            this.pageInput === undefined ||
            String(
                this.pageInput,
            ).trim() === ""
        ) {

            this.pageInput =
                this.currentPage;

            return;
        }


        const requestedPage =
            Number(
                this.pageInput,
            );


        if (
            !Number.isFinite(
                requestedPage,
            )
        ) {

            this.pageInput =
                this.currentPage;

            return;
        }


        const visibleRequested = this.toVisiblePage(requestedPage);
        if (!Number.isInteger(visibleRequested) || visibleRequested < 1 || visibleRequested > this.visibleTotalPages) {
            this.pageInput = this.currentPage;
            return;
        }
        if (this.hasGlobalPagination && (requestedPage < 1 || requestedPage > this.totalPages)) {
            this.pageInput = this.currentPage;
            this.globalPageRequest.emit(visibleRequested);
            return;
        }

        const targetPage =
            Math.max(
                1,
                Math.min(
                    Math.floor(
                        requestedPage,
                    ),
                    this.totalPages,
                ),
            );


        this.pageInput =
            targetPage;


        if (
            targetPage ===
            this.currentPage
        ) {

            return;
        }


        this.currentPage =
            targetPage;


        this.clearSelection();


        await this.renderCurrentPageWithZoomMode();


        this.pageChange.emit(
            this.currentPage,
        );
    }


    async onPageInputKeydown(
        event: KeyboardEvent,
    ) {

        if (
            event.key !==
            "Enter"
        ) {

            return;
        }


        event.preventDefault();


        await this.goToPage();


        const input =
            event.target as HTMLInputElement;


        input.blur();
    }


    /*
     * Renderiza la página respetando el modo de zoom.
     *
     * Es especialmente útil al navegar entre páginas
     * que puedan tener tamaños u orientaciones distintas.
     */
    private async renderCurrentPageWithZoomMode() {

        if (
            this.zoomMode ===
            "width"
        ) {

            return await this.fitToWidth();
        }


        if (
            this.zoomMode ===
            "page"
        ) {

            return await this.fitToPage();
        }


        return await this.renderPage(
            this.currentPage,
        );
    }


    /*
     * =========================================================
     * ZOOM
     * =========================================================
     */

    async zoomIn() {

        /*
         * Al utilizar + / - abandonamos cualquier
         * ajuste automático y pasamos a zoom manual.
         */
        this.zoomMode =
            "custom";


        this.scale =
            Math.min(
                this.scale + 0.2,
                5,
            );


        this.clearSelection();


        await this.renderPage(
            this.currentPage,
        );
    }


    async zoomOut() {

        if (
            this.scale <= 0.4
        ) {

            return;
        }


        this.zoomMode =
            "custom";


        this.scale =
            Math.max(
                this.scale - 0.2,
                0.4,
            );


        this.clearSelection();


        await this.renderPage(
            this.currentPage,
        );
    }


    /*
     * Ajusta el PDF al ancho disponible
     * dentro de la zona de lectura.
     */
    async fitToWidth() {
        const request = this.annotationNavigationRevision;
        const documentRevision = this.documentRevision;
        const pageNumber = this.currentPage;

        if (
            !this.pdfDoc ||
            !this.pdfAreaRef
        ) {

            return;
        }


        try {

            const page =
                await this.pdfDoc
                    .getPage(
                        this.currentPage,
                    );


            if (request !== this.annotationNavigationRevision || documentRevision !== this.documentRevision ||
                pageNumber !== this.currentPage) { return; }

            /*
             * Obtenemos el tamaño real de la página
             * usando escala 1.
             */
            const viewport =
                page.getViewport({
                    scale: 1,
                });


            const pdfArea =
                this.pdfAreaRef
                    .nativeElement;


            /*
             * Dejamos un pequeño margen para que
             * la página no quede pegada a los bordes.
             */
            const availableWidth =
                Math.max(
                    pdfArea.clientWidth - 24,
                    1,
                );


            this.scale =
                this.clampScale(
                    availableWidth /
                    viewport.width,
                );


            this.zoomMode =
                "width";


            this.clearSelection();


            return await this.renderPage(
                this.currentPage,
            );

        } catch (error) {

            console.error(
                "Error ajustando el PDF al ancho:",
                error,
            );
        }
    }


    /*
     * Ajusta la página completa al espacio visible,
     * teniendo en cuenta tanto el ancho como el alto.
     */
    async fitToPage() {
        const request = this.annotationNavigationRevision;
        const documentRevision = this.documentRevision;
        const pageNumber = this.currentPage;

        if (
            !this.pdfDoc ||
            !this.pdfAreaRef
        ) {

            return;
        }


        try {

            const page =
                await this.pdfDoc
                    .getPage(
                        this.currentPage,
                    );


            const viewport =
                page.getViewport({
                    scale: 1,
                });


            const pdfArea =
                this.pdfAreaRef
                    .nativeElement;


            const availableWidth =
                Math.max(
                    pdfArea.clientWidth - 24,
                    1,
                );


            const availableHeight =
                Math.max(
                    pdfArea.clientHeight - 24,
                    1,
                );


            const widthScale =
                availableWidth /
                viewport.width;


            const heightScale =
                availableHeight /
                viewport.height;

            if (request !== this.annotationNavigationRevision || documentRevision !== this.documentRevision ||
                pageNumber !== this.currentPage) { return; }


            this.scale =
                this.clampScale(
                    Math.min(
                        widthScale,
                        heightScale,
                    ),
                );


            this.zoomMode =
                "page";


            this.clearSelection();


            return await this.renderPage(
                this.currentPage,
            );

        } catch (error) {

            console.error(
                "Error ajustando la página:",
                error,
            );
        }
    }


    /*
     * Mantiene los ajustes automáticos entre el 40 % y el 300 %.
     */
    private clampScale(
        value: number,
    ): number {

        return Math.min(
            Math.max(
                value,
                0.4,
            ),
            3,
        );
    }


    /*
     * =========================================================
     * HERRAMIENTAS
     * =========================================================
     */

    setTool(
        tool: PdfTool,
    ) {
        if (this.annotationsLoadError || this.pdfLoadError || (this.editingLocked && tool !== 'none')) { return; }

        this.currentTool =
            tool;


        this.clearSelection();


        this.draftHighlight =
            null;

        this.draftInk =
            null;

        this.resizingAnnotationId =
            null;

        this.resizingAnnotationType =
            null;

        this.resizeDirection =
            null;

        this.resizeStartPoint =
            null;

        this.isDragging =
            false;
    }


    clearSelection() {
        this.finishAnnotationEdit();

        this.selectedAnnotationId =
            null;

        this.selectedAnnotation =
            null;
    }


    /*
     * =========================================================
     * EVENTOS SOBRE EL PDF
     * =========================================================
     */

    isPanning = false;
    private panStart: { x: number; y: number; left: number; top: number } | null = null;
    private panClickTimer: any = null;

    get canPan(): boolean {
        return (this.currentTool === 'none' || this.currentTool === 'move') &&
            !this.isDragging && !this.movingAnnotationId && !this.resizingAnnotationId;
    }

    private preparePan(event: MouseEvent) {
        const target = event.target as HTMLElement;
        const page = target.closest('.page-wrapper');
        const interactive = target.closest('button, a, summary, label, [role="button"], [role="textbox"], [tabindex]');
        if (event.button !== 0 || event.defaultPrevented || !this.canPan ||
            !page || target.isContentEditable || (interactive && page.contains(interactive)) ||
            target.closest('.highlight-rect, .ink-line, .ink-hitbox, .note-box, .free-text-box, .resize-handle, input, textarea, select, [contenteditable]')) { return; }

        this.cancelPan();
        const area = this.pdfAreaRef.nativeElement;
        this.panStart = { x: event.clientX, y: event.clientY, left: area.scrollLeft, top: area.scrollTop };
        window.addEventListener('mousemove', this.movePan, true);
        window.addEventListener('mouseup', this.endPan, true);
        window.addEventListener('blur', this.cancelPan);
    }

    private movePan = (event: MouseEvent) => {
        if (!this.panStart) { return; }
        if (!(event.buttons & 1) || !this.canPan) { this.cancelPan(); return; }
        const dx = event.clientX - this.panStart.x;
        const dy = event.clientY - this.panStart.y;
        if (!this.isPanning && dx * dx + dy * dy <= 16) { return; }
        if (!this.isPanning) {
            this.isPanning = true;
            window.addEventListener('click', this.suppressPanClick, true);
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        const area = this.pdfAreaRef.nativeElement;
        area.scrollLeft = this.panStart.left - dx;
        area.scrollTop = this.panStart.top - dy;
    };

    private suppressPanClick = (event: MouseEvent) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.clearPanClick();
    };

    private clearPanClick() {
        clearTimeout(this.panClickTimer);
        this.panClickTimer = null;
        window.removeEventListener('click', this.suppressPanClick, true);
    }

    private endPan = (event: MouseEvent) => {
        if (event.button !== 0) { return; }
        const wasPanning = this.isPanning;
        this.releasePan();
        if (wasPanning) {
            event.preventDefault();
            event.stopImmediatePropagation();
            // El click nativo sigue al mouseup; no bloquear el siguiente clic independiente.
            this.panClickTimer = setTimeout(() => this.clearPanClick(), 0);
        }
    };

    private releasePan() {
        this.panStart = null;
        this.isPanning = false;
        window.removeEventListener('mousemove', this.movePan, true);
        window.removeEventListener('mouseup', this.endPan, true);
        window.removeEventListener('blur', this.cancelPan);
    }

    private cancelPan = () => {
        this.releasePan();
        this.clearPanClick();
    };

    onPageMouseDown(
        event: MouseEvent,
    ) {

        // Las anotaciones y sus tiradores gestionan sus propios eventos.
        const target = event.target as Element;
        if (target.closest(".highlight-rect, .ink-line, .ink-hitbox, .note-box, .free-text-box")) {
            return;
        }

        this.preparePan(event);

        // El primer clic en vacío cierra la edición; el siguiente puede crear.
        if (this.selectedAnnotation) {
            this.clearSelection();
            return;
        }

        if (
            this.currentTool ===
            "highlight"
        ) {

            this.startHighlight(
                event,
            );
        }


        if (
            this.currentTool ===
            "ink"
        ) {

            this.startInk(
                event,
            );
        }


        if (
            this.currentTool ===
            "note"
        ) {

            this.createNote(
                event,
            );
        }


        if (
            this.currentTool ===
            "freeText"
        ) {

            this.createFreeText(
                event,
            );
        }
    }


    onPageMouseMove(
        event: MouseEvent,
    ) {

        if (
            this.resizingAnnotationId
        ) {

            if (
                this.resizingAnnotationType ===
                "freeText"
            ) {

                this.resizeFreeText(
                    event,
                );

            } else {

                this.resizeHighlight(
                    event,
                );
            }

            return;
        }


        if (
            this.movingAnnotationId
        ) {

            this.moveAnnotation(
                event,
            );

            return;
        }


        if (
            this.currentTool ===
            "highlight"
        ) {

            this.moveHighlight(
                event,
            );
        }


        if (
            this.currentTool ===
            "ink"
        ) {

            this.moveInk(
                event,
            );
        }
    }


    onPageMouseUp(
        event: MouseEvent,
    ) {

        if (
            this.resizingAnnotationId
        ) {

            if (
                this.resizingAnnotationType ===
                "freeText"
            ) {

                this.endResizeFreeText(
                    event,
                );

            } else {

                this.endResizeHighlight(
                    event,
                );
            }

            return;
        }


        if (
            this.movingAnnotationId
        ) {

            this.endMoveAnnotation(
                event,
            );

            return;
        }


        if (
            this.currentTool ===
            "highlight"
        ) {

            this.endHighlight(
                event,
            );
        }


        if (
            this.currentTool ===
            "ink"
        ) {

            this.endInk(
                event,
            );
        }
    }


    private getRelativePoint(
        event: MouseEvent,
    ): PdfPoint {

        const pageRect =
            this.canvasRef
                .nativeElement
                .getBoundingClientRect();


        return {

            x:
                (
                    event.clientX -
                    pageRect.left
                ) /
                pageRect.width,

            y:
                (
                    event.clientY -
                    pageRect.top
                ) /
                pageRect.height,
        };
    }


    /*
     * =========================================================
     * SUBRAYADO
     * =========================================================
     */

    startHighlight(
        event: MouseEvent,
    ) {
        this.finishAnnotationEdit();

        const target =
            event.target as HTMLElement;


        if (
            target.classList.contains(
                "highlight-rect",
            ) ||
            target.classList.contains(
                "ink-line",
            ) ||
            target.classList.contains(
                "ink-hitbox",
            )
        ) {

            return;
        }


        event.preventDefault();


        const p =
            this.getRelativePoint(
                event,
            );


        const now =
            new Date()
                .toISOString();


        this.isDragging =
            true;


        this.clearSelection();


        this.startX =
            p.x;

        this.startY =
            p.y;


        this.draftHighlight = {

            id:
                "draft_highlight",

            type:
                "highlight",

            page:
                this.currentPage,

            color:
                this.hexToRgba(
                    this.selectedColor,
                    0.4,
                ),

            x:
                this.startX,

            y:
                this.startY,

            w:
                0,

            h:
                0,

            createdAt:
                now,

            updatedAt:
                now,
        };
    }


    moveHighlight(
        event: MouseEvent,
    ) {

        if (
            !this.isDragging ||
            !this.draftHighlight
        ) {

            return;
        }


        event.preventDefault();


        const p =
            this.getRelativePoint(
                event,
            );


        const now =
            new Date()
                .toISOString();


        this.draftHighlight = {

            ...this.draftHighlight,

            x:
                Math.min(
                    this.startX,
                    p.x,
                ),

            y:
                Math.min(
                    this.startY,
                    p.y,
                ),

            w:
                Math.abs(
                    p.x -
                    this.startX,
                ),

            h:
                Math.abs(
                    p.y -
                    this.startY,
                ),

            updatedAt:
                now,
        };
    }


    endHighlight(
        event: MouseEvent,
    ) {

        if (
            !this.isDragging ||
            !this.draftHighlight
        ) {

            return;
        }


        event.preventDefault();


        this.isDragging =
            false;


        if (
            this.draftHighlight.w >
                0.005 &&
            this.draftHighlight.h >
                0.005
        ) {

            const now =
                new Date()
                    .toISOString();


            const finalHighlight:
                HighlightAnnotation = {

                ...this.draftHighlight,

                id:
                    "h_" +
                    Date.now(),

                updatedAt:
                    now,
            };


            this.annotations.push(
                finalHighlight,
            );


            this.selectedAnnotationId =
                finalHighlight.id;

            this.selectedAnnotation =
                finalHighlight;


            this.notifyAnnotationsChange();
        }


        this.draftHighlight =
            null;
    }


    /*
     * =========================================================
     * DIBUJO
     * =========================================================
     */

    startInk(
        event: MouseEvent,
    ) {
        this.finishAnnotationEdit();

        const target =
            event.target as HTMLElement;


        if (
            target.classList.contains(
                "highlight-rect",
            ) ||
            target.classList.contains(
                "ink-line",
            ) ||
            target.classList.contains(
                "ink-hitbox",
            )
        ) {

            return;
        }


        event.preventDefault();


        const p =
            this.getRelativePoint(
                event,
            );


        const now =
            new Date()
                .toISOString();


        this.isDragging =
            true;


        this.clearSelection();


        this.draftInk = {

            id:
                "ink_" +
                Date.now(),

            type:
                "ink",

            page:
                this.currentPage,

            color:
                this.selectedColor,

            strokeWidth:
                this.selectedStrokeWidth,

            points:
                [p],

            createdAt:
                now,

            updatedAt:
                now,
        };
    }


    moveInk(
        event: MouseEvent,
    ) {

        if (
            !this.isDragging ||
            !this.draftInk
        ) {

            return;
        }


        event.preventDefault();


        const p =
            this.getRelativePoint(
                event,
            );


        this.draftInk = {

            ...this.draftInk,

            points: [
                ...this.draftInk.points,
                p,
            ],

            updatedAt:
                new Date()
                    .toISOString(),
        };
    }


    endInk(
        event: MouseEvent,
    ) {

        if (
            !this.isDragging ||
            !this.draftInk
        ) {

            return;
        }


        event.preventDefault();


        this.isDragging =
            false;


        if (
            this.draftInk
                .points
                .length > 2
        ) {

            const finalInk:
                InkAnnotation = {

                ...this.draftInk,

                updatedAt:
                    new Date()
                        .toISOString(),
            };


            this.annotations.push(
                finalInk,
            );


            this.selectedAnnotationId =
                finalInk.id;

            this.selectedAnnotation =
                finalInk;


            this.notifyAnnotationsChange();
        }


        this.draftInk =
            null;
    }


    /*
     * =========================================================
     * SELECCIÓN
     * =========================================================
     */

    selectAnnotation(
        event: MouseEvent | null,
        annotation: PdfAnnotation,
    ) {
        this.finishAnnotationEdit();

        if (event) {
            this.cancelAnnotationNavigation();
            event.preventDefault();
            event.stopPropagation();
        }


        this.isDragging =
            false;

        this.draftHighlight =
            null;

        this.draftInk =
            null;


        this.selectedAnnotationId =
            annotation.id;

        this.selectedAnnotation =
            annotation;
    }


    /*
     * =========================================================
     * ELIMINAR
     * =========================================================
     */

    deleteSelectedAnnotation() {
        this.finishAnnotationEdit();

        if (
            !this.selectedAnnotationId
        ) {

            return;
        }


        this.annotations =
            this.annotations.filter(
                (annotation) =>
                    annotation.id !==
                    this.selectedAnnotationId,
            );


        this.clearSelection();


        this.notifyAnnotationsChange();
    }


    /*
     * =========================================================
     * MODIFICACIÓN DE PROPIEDADES
     * =========================================================
     */


    /*
     * Actualiza una anotación desde
     * el panel lateral.
     *
     * El objeto selectedAnnotation pertenece
     * directamente al array annotations, por lo que
     * el cambio se aplica inmediatamente en memoria.
     *
     * Retrasamos únicamente la notificación al padre
     * para evitar una emisión por cada tecla.
     */
    updateSelectedAnnotation(field: string = "text") {
        this.beginAnnotationEdit(field);
        this.scheduleHistoryEdit();

        if (
            !this.selectedAnnotation
        ) {

            return;
        }


        /*
         * Actualizamos la fecha inmediatamente.
         */
        this.selectedAnnotation
            .updatedAt =
            new Date()
                .toISOString();


        /*
         * Cancelamos una notificación anterior
         * si el usuario continúa escribiendo.
         */
        if (
            this.annotationEditTimeout
        ) {

            clearTimeout(
                this.annotationEditTimeout,
            );
        }


        /*
         * Esperamos unos milisegundos desde
         * la última modificación antes de comunicar
         * el cambio al componente padre.
         */
        this.annotationEditTimeout =
            setTimeout(
                () => {

                    this.annotationEditTimeout =
                        null;


                    this.notifyAnnotationsChange();

                },
                this.annotationEditDelay,
            );

        if (field === "fontFamily" || field === "fontSize") {
            this.finishAnnotationEdit();
        }
    }


    /*
     * =========================================================
     * FILTROS POR PÁGINA
     * =========================================================
     */

    getCurrentPageHighlights():
        HighlightAnnotation[] {

        return this.annotations
            .filter(
                (
                    annotation,
                ): annotation is HighlightAnnotation =>
                    annotation.page ===
                        this.currentPage &&
                    annotation.type ===
                        "highlight",
            );
    }


    getCurrentPageInkAnnotations():
        InkAnnotation[] {

        return this.annotations
            .filter(
                (
                    annotation,
                ): annotation is InkAnnotation =>
                    annotation.page ===
                        this.currentPage &&
                    annotation.type ===
                        "ink",
            );
    }


    getCurrentPageNotes():
        NoteAnnotation[] {

        return this.annotations
            .filter(
                (
                    annotation,
                ): annotation is NoteAnnotation =>
                    annotation.page ===
                        this.currentPage &&
                    annotation.type ===
                        "note",
            );
    }


    getCurrentPageFreeTexts():
        FreeTextAnnotation[] {

        return this.annotations
            .filter(
                (
                    annotation,
                ): annotation is FreeTextAnnotation =>
                    annotation.page ===
                        this.currentPage &&
                    annotation.type ===
                        "freeText",
            );
    }


    /*
     * =========================================================
     * ESTILOS
     * =========================================================
     */

    getHighlightStyle(
        rect: HighlightAnnotation,
    ) {

        return {

            left:
                rect.x *
                    100 +
                "%",

            top:
                rect.y *
                    100 +
                "%",

            width:
                rect.w *
                    100 +
                "%",

            height:
                rect.h *
                    100 +
                "%",

            background:
                rect.color,
        };
    }


    getInkPoints(
        ink: InkAnnotation,
    ): string {

        return ink.points
            .map(
                (p: PdfPoint) =>
                    p.x +
                    "," +
                    p.y,
            )
            .join(
                " ",
            );
    }


    getNoteStyle(
        note: NoteAnnotation,
    ) {

        return {

            left:
                note.x *
                    100 +
                "%",

            top:
                note.y *
                    100 +
                "%",

            background:
                note.color,
        };
    }


    getFreeTextStyle(
        annotation: FreeTextAnnotation,
    ) {

        return {

            left:
                annotation.x *
                    100 +
                "%",

            top:
                annotation.y *
                    100 +
                "%",

            width:
                annotation.width *
                    100 +
                "%",

            height:
                annotation.height *
                    100 +
                "%",

            color:
                annotation.color,

            fontSize:
                annotation.fontSize +
                "px",

            fontFamily:
                annotation.fontFamily,
        };
    }


    /*
     * =========================================================
     * NOTAS
     * =========================================================
     */

    createNote(
        event: MouseEvent,
    ) {
        this.finishAnnotationEdit();

        const target =
            event.target as HTMLElement;


        if (
            target.classList.contains(
                "highlight-rect",
            ) ||
            target.classList.contains(
                "ink-line",
            ) ||
            target.classList.contains(
                "ink-hitbox",
            ) ||
            target.classList.contains(
                "note-box",
            ) ||
            target.classList.contains(
                "free-text-box",
            )
        ) {

            return;
        }


        event.preventDefault();


        const p =
            this.getRelativePoint(
                event,
            );


        const now =
            new Date()
                .toISOString();


        const note:
            NoteAnnotation = {

            id:
                "note_" +
                Date.now(),

            type:
                "note",

            page:
                this.currentPage,

            color:
                this.selectedColor,

            x:
                p.x,

            y:
                p.y,

            text:
                "",

            createdAt:
                now,

            updatedAt:
                now,
        };


        this.annotations.push(
            note,
        );


        this.selectedAnnotationId =
            note.id;

        this.selectedAnnotation =
            note;


        /*
         * La herramienta vuelve automáticamente
         * al modo normal para evitar crear varias
         * notas accidentalmente.
         */
        this.currentTool =
            "none";


        this.notifyAnnotationsChange();
    }


    /*
     * =========================================================
     * TEXTO LIBRE
     * =========================================================
     */

    createFreeText(
        event: MouseEvent,
    ) {
        this.finishAnnotationEdit();

        const target =
            event.target as HTMLElement;


        if (
            target.classList.contains(
                "highlight-rect",
            ) ||
            target.classList.contains(
                "ink-line",
            ) ||
            target.classList.contains(
                "ink-hitbox",
            ) ||
            target.classList.contains(
                "note-box",
            ) ||
            target.classList.contains(
                "free-text-box",
            )
        ) {

            return;
        }


        event.preventDefault();


        const p =
            this.getRelativePoint(
                event,
            );


        const now =
            new Date()
                .toISOString();


        const freeText:
            FreeTextAnnotation = {

            id:
                "txt_" +
                Date.now(),

            type:
                "freeText",

            page:
                this.currentPage,

            color:
                this.selectedColor,

            x:
                p.x,

            y:
                p.y,

            width:
                0.22,

            height:
                0.05,

            text:
                "",

            fontSize:
                16,

            fontFamily:
                "Helvetica",

            createdAt:
                now,

            updatedAt:
                now,
        };


        this.annotations.push(
            freeText,
        );


        this.selectedAnnotationId =
            freeText.id;

        this.selectedAnnotation =
            freeText;


        /*
         * Volvemos al modo normal
         * después de crear el texto.
         */
        this.currentTool =
            "none";


        this.notifyAnnotationsChange();
    }


    /*
     * En la versión 1 el doble clic no edita directamente
     * sobre el PDF. Simplemente selecciona el texto para
     * editarlo desde el panel lateral.
     */
    editFreeText(
        event: MouseEvent,
        annotation: FreeTextAnnotation,
    ) {

        event.preventDefault();

        event.stopPropagation();


        this.selectedAnnotationId =
            annotation.id;

        this.selectedAnnotation =
            annotation;
    }


    /*
     * =========================================================
     * MOVER ANOTACIONES
     * =========================================================
     */

    startMoveAnnotation(
        event: MouseEvent,
        annotation: PdfAnnotation,
    ) {
        this.finishAnnotationEdit();
        this.cancelAnnotationNavigation();

        if (
            annotation.type !==
                "highlight" &&
            annotation.type !==
                "note" &&
            annotation.type !==
                "freeText"
        ) {

            return;
        }


        event.preventDefault();

        event.stopPropagation();


        const p =
            this.getRelativePoint(
                event,
            );


        this.selectedAnnotationId =
            annotation.id;

        this.selectedAnnotation =
            annotation;


        this.movingAnnotationId =
            annotation.id;


        this.moveStartPoint =
            p;


        this.moveOriginalX =
            annotation.x;

        this.moveOriginalY =
            annotation.y;


        this.isDragging =
            true;
    }


    moveAnnotation(
        event: MouseEvent,
    ) {

        if (
            !this.isDragging ||
            !this.movingAnnotationId ||
            !this.moveStartPoint
        ) {

            return;
        }


        event.preventDefault();


        const p =
            this.getRelativePoint(
                event,
            );


        const dx =
            p.x -
            this.moveStartPoint.x;

        const dy =
            p.y -
            this.moveStartPoint.y;


        const annotation =
            this.annotations.find(
                (a) =>
                    a.id ===
                    this.movingAnnotationId,
            );


        if (!annotation) {

            return;
        }


        if (
            annotation.type ===
                "highlight"
        ) {

            annotation.x =
                Math.max(
                    0,
                    Math.min(
                        this.moveOriginalX +
                        dx,
                        1 -
                        annotation.w,
                    ),
                );

            annotation.y =
                Math.max(
                    0,
                    Math.min(
                        this.moveOriginalY +
                        dy,
                        1 -
                        annotation.h,
                    ),
                );

            annotation.updatedAt =
                new Date()
                    .toISOString();

        } else if (
            annotation.type ===
                "freeText"
        ) {

            annotation.x =
                Math.max(
                    0,
                    Math.min(
                        this.moveOriginalX +
                        dx,
                        1 -
                        annotation.width,
                    ),
                );

            annotation.y =
                Math.max(
                    0,
                    Math.min(
                        this.moveOriginalY +
                        dy,
                        1 -
                        annotation.height,
                    ),
                );

            annotation.updatedAt =
                new Date()
                    .toISOString();

        } else if (
            annotation.type ===
                "note"
        ) {

            /*
             * Las notas se posicionan por su centro.
             * Dejamos un pequeño margen para que no
             * desaparezcan parcialmente fuera de la página.
             */
            const margin =
                0.02;

            annotation.x =
                Math.max(
                    margin,
                    Math.min(
                        this.moveOriginalX +
                        dx,
                        1 -
                        margin,
                    ),
                );

            annotation.y =
                Math.max(
                    margin,
                    Math.min(
                        this.moveOriginalY +
                        dy,
                        1 -
                        margin,
                    ),
                );

            annotation.updatedAt =
                new Date()
                    .toISOString();
        }
    }


    endMoveAnnotation(
        event: MouseEvent,
    ) {

        if (
            !this.isDragging ||
            !this.movingAnnotationId
        ) {

            return;
        }


        event.preventDefault();


        this.isDragging =
            false;

        this.movingAnnotationId =
            null;

        this.moveStartPoint =
            null;


        this.notifyAnnotationsChange();
    }


    /*
     * =========================================================
     * REDIMENSIONAR SUBRAYADOS
     * =========================================================
     */

    startResizeHighlight(
        event: MouseEvent,
        annotation: HighlightAnnotation,
        direction:
            "n" | "s" | "e" | "w" |
            "ne" | "nw" | "se" | "sw",
    ) {
        this.finishAnnotationEdit();

        event.preventDefault();

        event.stopPropagation();


        const p =
            this.getRelativePoint(
                event,
            );


        this.selectedAnnotationId =
            annotation.id;

        this.selectedAnnotation =
            annotation;


        this.resizingAnnotationId =
            annotation.id;

        this.resizingAnnotationType =
            "highlight";

        this.resizeDirection =
            direction;

        this.resizeStartPoint =
            p;


        this.resizeOriginalX =
            annotation.x;

        this.resizeOriginalY =
            annotation.y;

        this.resizeOriginalW =
            annotation.w;

        this.resizeOriginalH =
            annotation.h;


        this.isDragging =
            true;
    }


    resizeHighlight(
        event: MouseEvent,
    ) {

        if (
            !this.isDragging ||
            !this.resizingAnnotationId ||
            this.resizingAnnotationType !== "highlight" ||
            !this.resizeDirection ||
            !this.resizeStartPoint
        ) {

            return;
        }


        event.preventDefault();


        const annotation =
            this.annotations.find(
                (a) =>
                    a.id ===
                    this.resizingAnnotationId &&
                    a.type ===
                    "highlight",
            ) as HighlightAnnotation | undefined;


        if (!annotation) {

            return;
        }


        const p =
            this.getRelativePoint(
                event,
            );


        const dx =
            p.x -
            this.resizeStartPoint.x;

        const dy =
            p.y -
            this.resizeStartPoint.y;


        const minWidth =
            0.01;

        const minHeight =
            0.01;


        let x =
            this.resizeOriginalX;

        let y =
            this.resizeOriginalY;

        let w =
            this.resizeOriginalW;

        let h =
            this.resizeOriginalH;


        if (
            this.resizeDirection.indexOf(
                "e",
            ) !== -1
        ) {

            w =
                Math.max(
                    minWidth,
                    Math.min(
                        this.resizeOriginalW +
                        dx,
                        1 -
                        this.resizeOriginalX,
                    ),
                );
        }


        if (
            this.resizeDirection.indexOf(
                "s",
            ) !== -1
        ) {

            h =
                Math.max(
                    minHeight,
                    Math.min(
                        this.resizeOriginalH +
                        dy,
                        1 -
                        this.resizeOriginalY,
                    ),
                );
        }


        if (
            this.resizeDirection.indexOf(
                "w",
            ) !== -1
        ) {

            const right =
                this.resizeOriginalX +
                this.resizeOriginalW;


            x =
                Math.max(
                    0,
                    Math.min(
                        this.resizeOriginalX +
                        dx,
                        right -
                        minWidth,
                    ),
                );


            w =
                right -
                x;
        }


        if (
            this.resizeDirection.indexOf(
                "n",
            ) !== -1
        ) {

            const bottom =
                this.resizeOriginalY +
                this.resizeOriginalH;


            y =
                Math.max(
                    0,
                    Math.min(
                        this.resizeOriginalY +
                        dy,
                        bottom -
                        minHeight,
                    ),
                );


            h =
                bottom -
                y;
        }


        annotation.x =
            x;

        annotation.y =
            y;

        annotation.w =
            w;

        annotation.h =
            h;

        annotation.updatedAt =
            new Date()
                .toISOString();
    }


    endResizeHighlight(
        event: MouseEvent,
    ) {

        if (
            !this.isDragging ||
            !this.resizingAnnotationId
        ) {

            return;
        }


        event.preventDefault();


        this.isDragging =
            false;

        this.resizingAnnotationId =
            null;

        this.resizingAnnotationType =
            null;

        this.resizeDirection =
            null;

        this.resizeStartPoint =
            null;


        this.notifyAnnotationsChange();
    }


    /*
     * =========================================================
     * REDIMENSIONAR TEXTO LIBRE
     * =========================================================
     */

    startResizeFreeText(
        event: MouseEvent,
        annotation: FreeTextAnnotation,
        direction:
            "n" | "s" | "e" | "w" |
            "ne" | "nw" | "se" | "sw",
    ) {
        this.finishAnnotationEdit();

        event.preventDefault();

        event.stopPropagation();


        const p =
            this.getRelativePoint(
                event,
            );


        this.selectedAnnotationId =
            annotation.id;

        this.selectedAnnotation =
            annotation;


        this.resizingAnnotationId =
            annotation.id;

        this.resizingAnnotationType =
            "freeText";

        this.resizeDirection =
            direction;

        this.resizeStartPoint =
            p;


        this.resizeOriginalX =
            annotation.x;

        this.resizeOriginalY =
            annotation.y;

        this.resizeOriginalW =
            annotation.width;

        this.resizeOriginalH =
            annotation.height;


        this.isDragging =
            true;
    }


    resizeFreeText(
        event: MouseEvent,
    ) {

        if (
            !this.isDragging ||
            !this.resizingAnnotationId ||
            this.resizingAnnotationType !== "freeText" ||
            !this.resizeDirection ||
            !this.resizeStartPoint
        ) {

            return;
        }


        event.preventDefault();


        const annotation =
            this.annotations.find(
                (a) =>
                    a.id ===
                    this.resizingAnnotationId &&
                    a.type ===
                    "freeText",
            ) as FreeTextAnnotation | undefined;


        if (!annotation) {

            return;
        }


        const p =
            this.getRelativePoint(
                event,
            );


        const dx =
            p.x -
            this.resizeStartPoint.x;

        const dy =
            p.y -
            this.resizeStartPoint.y;


        /*
         * Evitamos que la caja de texto llegue
         * a tener un tamaño prácticamente nulo.
         */
        const minWidth =
            0.04;

        const minHeight =
            0.025;


        let x =
            this.resizeOriginalX;

        let y =
            this.resizeOriginalY;

        let width =
            this.resizeOriginalW;

        let height =
            this.resizeOriginalH;


        if (
            this.resizeDirection.indexOf(
                "e",
            ) !== -1
        ) {

            width =
                Math.max(
                    minWidth,
                    Math.min(
                        this.resizeOriginalW +
                        dx,
                        1 -
                        this.resizeOriginalX,
                    ),
                );
        }


        if (
            this.resizeDirection.indexOf(
                "s",
            ) !== -1
        ) {

            height =
                Math.max(
                    minHeight,
                    Math.min(
                        this.resizeOriginalH +
                        dy,
                        1 -
                        this.resizeOriginalY,
                    ),
                );
        }


        if (
            this.resizeDirection.indexOf(
                "w",
            ) !== -1
        ) {

            const right =
                this.resizeOriginalX +
                this.resizeOriginalW;


            x =
                Math.max(
                    0,
                    Math.min(
                        this.resizeOriginalX +
                        dx,
                        right -
                        minWidth,
                    ),
                );


            width =
                right -
                x;
        }


        if (
            this.resizeDirection.indexOf(
                "n",
            ) !== -1
        ) {

            const bottom =
                this.resizeOriginalY +
                this.resizeOriginalH;


            y =
                Math.max(
                    0,
                    Math.min(
                        this.resizeOriginalY +
                        dy,
                        bottom -
                        minHeight,
                    ),
                );


            height =
                bottom -
                y;
        }


        annotation.x =
            x;

        annotation.y =
            y;

        annotation.width =
            width;

        annotation.height =
            height;

        annotation.updatedAt =
            new Date()
                .toISOString();
    }


    endResizeFreeText(
        event: MouseEvent,
    ) {

        if (
            !this.isDragging ||
            !this.resizingAnnotationId ||
            this.resizingAnnotationType !== "freeText"
        ) {

            return;
        }


        event.preventDefault();


        this.isDragging =
            false;

        this.resizingAnnotationId =
            null;

        this.resizingAnnotationType =
            null;

        this.resizeDirection =
            null;

        this.resizeStartPoint =
            null;


        this.notifyAnnotationsChange();
    }


    /*
     * =========================================================
     * JSON DEL VISOR
     * =========================================================
     */

    private normalizeInkStrokeWidths(items: PdfAnnotation[]): PdfAnnotation[] {
        return items.map(annotation => {
            if (!annotation || annotation.type !== "ink") {
                return annotation;
            }

            const width = annotation.strokeWidth;
            return {
                ...annotation,
                strokeWidth:
                    typeof width === "number" && Number.isFinite(width) && width > 0
                        ? width
                        : 2,
            };
        });
    }

    loadAnnotationsFromJson() {
        this.resetAnnotationHistory();
        this.clearSelection();
        this.lastEmittedAnnotationsJson = null;
        const result = readAnnotationData(this.annotationsJson);
        this.annotationsLoadError = result.error;
        this.loadedAnnotationContext = this.annotationContext;
        this.annotations = result.error ? [] : this.normalizeInkStrokeWidths(result.annotations);
        if (result.error) { this.currentTool = "none"; }
        this.resetAnnotationHistory();
    }


    /*
     * Este es el JSON que se guarda
     * posteriormente en customData /
     * customDataGroup.
     */
    getAnnotationsJson():
        string {

        const data = {

            version:
                1,

            annotations:
                this.annotations,
        };


        return JSON.stringify(
            data,
        );
    }


    /*
     * Notifica al componente padre que las anotaciones
     * han cambiado.
     *
     * Guardamos el JSON emitido para reconocer después
     * el mismo valor cuando vuelva mediante @Input().
     */
    private notifyAnnotationsChange() {
        if (this.annotationsLoadError || this.pdfLoadError) { return; }
        this.recordHistory();

        const json =
            this.getAnnotationsJson();


        /*
         * Guardamos exactamente el mismo string
         * que vamos a emitir.
         */
        this.lastEmittedAnnotationsJson =
            json;


        this.annotationsChange.emit(
            json,
        );
        this.annotationsStateChange.emit({ json, context: this.loadedAnnotationContext });
    }


    /*
     * =========================================================
     * HERRAMIENTAS DE DEPURACIÓN / DESARROLLO
     *
     * Estas funciones no forman parte de la interfaz normal
     * del visor, pero se mantienen intencionadamente porque
     * resultan útiles para:
     *
     * - inspeccionar el JSON de anotaciones;
     * - exportar un caso problemático;
     * - importar un JSON para reproducir errores.
     *
     * Mientras no existan botones visibles que las invoquen,
     * no afectan al funcionamiento normal del visor.
     * =========================================================
     */

    showJson() {

        const data:
            Pick<PdfAnnotationsState, 'bookId' | 'userId' | 'version' | 'annotations'> = {

            bookId:
                0,

            userId:
                0,

            version:
                1,

            annotations:
                this.annotations,
        };


        this.jsonOutput =
            JSON.stringify(
                data,
                null,
                2,
            );
    }


    exportJson() {

        const data:
            Pick<PdfAnnotationsState, 'bookId' | 'userId' | 'version' | 'annotations'> = {

            bookId:
                0,

            userId:
                0,

            version:
                1,

            annotations:
                this.annotations,
        };


        const json =
            JSON.stringify(
                data,
                null,
                2,
            );


        const blob =
            new Blob(
                [json],
                {
                    type:
                        "application/json",
                },
            );


        const url =
            window.URL
                .createObjectURL(
                    blob,
                );


        const a =
            document.createElement(
                "a",
            );


        a.href =
            url;

        a.download =
            "annotations.json";


        a.click();


        window.URL
            .revokeObjectURL(
                url,
            );
    }


    importJson(
        event: Event,
    ) {

        const input =
            event.target as HTMLInputElement;


        if (
            !input.files ||
            input.files.length === 0
        ) {

            return;
        }


        const file =
            input.files[0];


        const reader =
            new FileReader();


        reader.onload =
            () => {

                try {

                    const content =
                        reader.result as string;


                    const parsed = readAnnotationData(content);
                    if (parsed.error || this.annotationsLoadError || this.pdfLoadError) {
                        alert("No se pueden importar anotaciones: el formato o la carga del capítulo no son válidos.");
                        return;
                    }
                    this.annotations = this.normalizeInkStrokeWidths(parsed.annotations);


                    this.resetAnnotationHistory();
                    this.clearSelection();


                    this.showJson();


                    this.notifyAnnotationsChange();

                } catch (error) {

                    console.error("No se han podido importar las anotaciones.");


                    alert(
                        "No se pudo importar el JSON.",
                    );

                } finally {

                    input.value =
                        "";
                }
            };


        reader.readAsText(
            file,
        );
    }


    /*
     * =========================================================
     * PANTALLA COMPLETA
     * =========================================================
     */

    async toggleFullscreen() {

        const element =
            this.viewerContainerRef
                .nativeElement as any;


        if (
            !document.fullscreenElement
        ) {

            if (
                element.requestFullscreen
            ) {

                await element
                    .requestFullscreen();


                this.isFullscreen =
                    true;
            }

        } else {

            if (
                document.exitFullscreen
            ) {

                await document
                    .exitFullscreen();


                this.isFullscreen =
                    false;
            }
        }
    }


    /*
     * =========================================================
     * COLORES
     * =========================================================
     */

    hexToRgba(
        hex: string,
        alpha: number,
    ): string {

        const r =
            parseInt(
                hex.substring(
                    1,
                    3,
                ),
                16,
            );


        const g =
            parseInt(
                hex.substring(
                    3,
                    5,
                ),
                16,
            );


        const b =
            parseInt(
                hex.substring(
                    5,
                    7,
                ),
                16,
            );


        return (
            `rgba(${r}, ${g}, ${b}, ${alpha})`
        );
    }


    changeSelectedStrokeWidth(width: number) {
        this.finishAnnotationEdit();
        const annotation = this.annotations.find(
            ann => ann.id === this.selectedAnnotationId,
        );

        if (!annotation || annotation.type !== "ink") {
            this.selectedStrokeWidth = width;
            return;
        }

        annotation.strokeWidth = width;
        annotation.updatedAt = new Date().toISOString();
        this.notifyAnnotationsChange();
    }

    changeSelectedColor(
        color: string,
    ) {

        if (!this.selectedAnnotation) {
            this.selectedColor = color;
        }


        if (
            !this.selectedAnnotationId
        ) {

            return;
        }


        const annotation =
            this.annotations.find(
                (ann) =>
                    ann.id ===
                    this.selectedAnnotationId,
            );


        if (!annotation) {

            return;
        }

        this.beginAnnotationEdit("color");
        this.scheduleHistoryEdit();


        if (
            annotation.type ===
            "highlight"
        ) {

            annotation.color =
                this.hexToRgba(
                    color,
                    0.4,
                );

        } else {

            annotation.color =
                color;
        }


        annotation.updatedAt =
            new Date()
                .toISOString();


        this.notifyAnnotationsChange();
    }
}
